import { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { Link } from "react-router-dom";
import { X, Building2, ArrowRight, Plus } from "lucide-react";
import { api } from "../lib/api";
import { startBoostCheckout } from "../lib/razorpay";
import { useAuth } from "../context/AuthContext";

/**
 * Modal that prompts the user to pick which of their properties they want to boost,
 * then triggers the Razorpay checkout.
 *
 * Props:
 *   open: boolean
 *   plan: { id, name, price, duration } — boost plan being purchased
 *   onClose: () => void
 *   onSuccess?: () => void
 */
export default function BoostPropertyPicker({ open, plan, onClose, onSuccess }) {
    const { user } = useAuth();
    const [properties, setProperties] = useState([]);
    const [loading, setLoading] = useState(false);
    const [paying, setPaying] = useState(false);
    const [selected, setSelected] = useState(null);

    useEffect(() => {
        if (!open) {
            setSelected(null);
            return;
        }
        document.body.style.overflow = "hidden";
        if (!user) return;
        setLoading(true);
        api.get("/partners/me/listings")
            .then((r) => setProperties(Array.isArray(r.data) ? r.data : (r.data?.items || [])))
            .catch(() => setProperties([]))
            .finally(() => setLoading(false));
        return () => {
            document.body.style.overflow = "";
        };
    }, [open, user]);

    const onPay = async () => {
        if (!selected || !plan) return;
        setPaying(true);
        const res = await startBoostCheckout(plan.id, selected);
        setPaying(false);
        if (res.ok) {
            onSuccess?.();
            onClose();
        }
    };

    if (!open || !plan) return null;
    return createPortal(
        <div className="fixed inset-0 z-[9999] overflow-y-auto overscroll-contain bg-black/70 backdrop-blur-sm" onClick={onClose}>
            <div className="min-h-full flex items-center justify-center p-4">
                <div className="w-full max-w-xl my-4 rounded-2xl bg-white border border-gold-500/30 shadow-2xl" onClick={(e) => e.stopPropagation()} data-testid="boost-picker">
                    {/* Header */}
                    <div className="sticky top-0 bg-white z-10 px-6 py-4 border-b border-navy-500/10 flex items-center justify-between rounded-t-2xl">
                        <div>
                            <h3 className="font-heading text-xl font-bold text-navy-500">Choose property to boost</h3>
                            <p className="text-xs text-navy-500/65 mt-0.5">{plan.name} · ₹{plan.price?.toLocaleString("en-IN")} · {plan.duration}</p>
                        </div>
                        <button onClick={onClose} className="text-navy-500/50 hover:text-navy-500 p-1" data-testid="boost-picker-close">
                            <X className="w-5 h-5" />
                        </button>
                    </div>

                    <div className="p-6">
                        {!user ? (
                            <div className="rounded-xl bg-[#FAFAF5] border border-navy-500/10 p-6 text-center">
                                <Building2 className="w-10 h-10 text-gold-500 mx-auto mb-2" />
                                <p className="text-navy-500/80 text-sm mb-4">Please sign in to boost your property.</p>
                                <Link to={`/login?next=/pricing`} onClick={onClose} className="inline-flex items-center gap-2 px-4 py-2.5 rounded-md bg-navy-500 text-white font-semibold text-sm">
                                    Sign in <ArrowRight className="w-4 h-4" />
                                </Link>
                            </div>
                        ) : loading ? (
                            <div className="space-y-3">
                                {Array.from({ length: 3 }).map((_, i) => (
                                    <div key={i} className="h-20 bg-[#FAFAF5] rounded-xl animate-pulse" />
                                ))}
                            </div>
                        ) : properties.length === 0 ? (
                            <div className="rounded-xl bg-[#FAFAF5] border border-navy-500/10 p-6 text-center">
                                <Building2 className="w-10 h-10 text-gold-500 mx-auto mb-2" />
                                <p className="text-navy-500/80 text-sm mb-4">You haven't listed any property yet. Add one first to boost it.</p>
                                <Link to="/add-property" onClick={onClose} data-testid="boost-picker-add-property" className="inline-flex items-center gap-2 px-4 py-2.5 rounded-md bg-gold-500 text-navy-900 font-semibold text-sm hover:bg-gold-100">
                                    <Plus className="w-4 h-4" /> Add a Property
                                </Link>
                            </div>
                        ) : (
                            <ul className="space-y-2 max-h-[55vh] overflow-y-auto pr-1">
                                {properties.map((p) => (
                                    <li key={p.id}>
                                        <label
                                            data-testid={`boost-property-option-${p.id}`}
                                            className={`flex items-center gap-3 p-3 rounded-xl border cursor-pointer transition-colors ${
                                                selected === p.id
                                                    ? "border-gold-500 bg-[#FFFBEF]"
                                                    : "border-navy-500/10 bg-white hover:border-gold-500/40"
                                            }`}
                                        >
                                            <input
                                                type="radio"
                                                name="boost-property"
                                                value={p.id}
                                                checked={selected === p.id}
                                                onChange={() => setSelected(p.id)}
                                                className="w-4 h-4 accent-gold-500 shrink-0"
                                            />
                                            <div className="w-14 h-14 rounded-lg bg-[#FAFAF5] overflow-hidden shrink-0">
                                                {p.images?.[0] && <img src={p.images[0]} alt="" className="w-full h-full object-cover" />}
                                            </div>
                                            <div className="flex-1 min-w-0">
                                                <div className="font-semibold text-navy-500 text-sm truncate">{p.title}</div>
                                                <div className="text-xs text-navy-500/55 mt-0.5 truncate">{p.city} · {p.property_type} · ₹{p.price?.toLocaleString("en-IN")}</div>
                                                {p.boost_active && <span className="text-[10px] uppercase tracking-wider font-bold text-gold-700">Already Boosted</span>}
                                            </div>
                                        </label>
                                    </li>
                                ))}
                            </ul>
                        )}

                        {user && properties.length > 0 && (
                            <button
                                type="button"
                                onClick={onPay}
                                disabled={!selected || paying}
                                data-testid="boost-picker-pay"
                                className="mt-5 w-full px-5 py-3.5 rounded-xl bg-gold-500 text-navy-900 font-bold text-sm tracking-wide hover:bg-gold-100 shadow-md hover:shadow-lg disabled:opacity-50 disabled:cursor-not-allowed transition-all"
                            >
                                {paying ? "Opening checkout…" : `Pay ₹${plan.price?.toLocaleString("en-IN")} & Boost Now`}
                            </button>
                        )}
                    </div>
                </div>
            </div>
        </div>,
        document.body
    );
}
